import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';

interface Role {
  id: string;
  name: string;
  value: string;
  category?: string;
}

interface ChangeRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChangeRole: (roleId: string) => Promise<void>;
  currentRoleId: string;
  roles: Role[];
}

// Role descriptions mapping based on role names from backend
// Backend sends "Agency" but we display as "Company"
const getRoleDescription = (roleName: string): string => {
  const descriptions: Record<string, string> = {
    // Developer roles
    'Developer / Admin': 'Internal Finderz team. Full system access to develop, deploy, and maintain the platform.',
    'Developer / Manager': 'Senior tech lead from the Finderz team. Oversees internal development roadmap.',
    
    // Agency/Company roles (backend sends Agency, we display as Company)
    'Agency / Admin': 'Third-party service provider. Full control over their own team, tasks, and progress.',
    'Agency / Manager': 'Manages workstreams and team members from the company side.',
    
    // Client roles
    'Client / Admin': 'The owner or key decision-maker from the customer side. Full access to team and project data.',
    'Client / Manager': 'Oversees part of the client\'s project. Collaborates with agencies and developers.',
    
    // Contractor role
    'Contractor': 'Specialist assigned to specific tasks. Access limited to work orders and documentation.',
    
    // Legacy role mappings (fallback)
    'admin': 'Administrator with full access to project resources and settings.',
    'manager': 'Manager with oversight responsibilities and team coordination access.',
    'user': 'Standard user with limited access to assigned tasks and documentation.',
    'owner': 'Project owner with complete control over project settings and team.',
    'member': 'Team member with access to shared resources and collaboration tools.',
    'viewer': 'Read-only access to project information and documentation.'
  };

  return descriptions[roleName] || 'Role description not available.';
};

// Get display name for role (rename Agency to Company)
const getDisplayRoleName = (roleName: string): string => {
  return roleName.replace('Agency', 'Company');
};

// Get category emoji based on role name (match Agency from backend)
const getCategoryEmoji = (roleName: string): string => {
  if (roleName.includes('Developer')) return '🔧';
  if (roleName.includes('Agency') || roleName.includes('Company')) return '🏢';
  if (roleName.includes('Client')) return '👤';
  if (roleName.includes('Contractor') || roleName === 'Contractor') return '🔨';
  
  // Legacy role emojis
  if (['admin', 'owner'].includes(roleName.toLowerCase())) return '👤';
  if (['manager', 'member'].includes(roleName.toLowerCase())) return '🏢';
  if (['user', 'viewer'].includes(roleName.toLowerCase())) return '🔨';
  
  return '👤'; // Default emoji
};

// Get category name based on role name (match Agency from backend, display as Company)
const getCategoryName = (roleName: string): string => {
  if (roleName.includes('Developer')) return 'Developer';
  if (roleName.includes('Agency') || roleName.includes('Company')) return 'Company';
  if (roleName.includes('Client')) return 'Client';
  if (roleName.includes('Contractor') || roleName === 'Contractor') return 'Contractor';
  
  // Legacy role categories
  if (['admin', 'owner'].includes(roleName.toLowerCase())) return 'Client';
  if (['manager', 'member'].includes(roleName.toLowerCase())) return 'Company';
  if (['user', 'viewer'].includes(roleName.toLowerCase())) return 'Contractor';
  
  return 'Other';
};

// Group roles by category
const groupRolesByCategory = (roles: Role[]) => {
  const grouped: Record<string, Role[]> = {};
  
  roles.forEach(role => {
    const category = getCategoryName(role.name);
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(role);
  });
  
  return grouped;
};

export const ChangeRoleModal: React.FC<ChangeRoleModalProps> = ({
  isOpen,
  onClose,
  onChangeRole,
  currentRoleId,
  roles
}) => {
  const { isFeatureEnabled } = useFeatureFlag();
  const [selectedRoleId, setSelectedRoleId] = useState(currentRoleId);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  const handleSubmit = async () => {
    if (selectedRoleId === currentRoleId) {
      onClose();
      return;
    }

    setIsLoading(true);
    try {
      await onChangeRole(selectedRoleId);
      toast.success('Role updated successfully');
      onClose();
    } catch (error) {
      toast.error('Failed to update role');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Filter out Developer roles in production
  const filteredRoles = isFeatureEnabled('SHOW_DEVELOPER_ROLES') 
    ? roles 
    : roles.filter(role => !role.name?.toLowerCase().includes('developer'));

  const groupedRoles = groupRolesByCategory(filteredRoles);
  // Developer roles at the bottom (only shown if feature is enabled)
  const categoryOrder = isFeatureEnabled('SHOW_DEVELOPER_ROLES') 
    ? ['Company', 'Client', 'Contractor', 'Other', 'Developer']
    : ['Company', 'Client', 'Contractor', 'Other'];

  return (
    <div 
      className="fixed inset-0 bg-white bg-opacity-80 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="fixed top-4 right-4 z-[60] text-[#aaa] hover:text-[#555] p-2"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div 
        className="bg-white rounded-lg w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto border border-[#eaeaea]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-[#eaeaea]">
          <h2 className="text-base font-semibold text-[#171717]">Change Role</h2>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Role Categories */}
          <div className="space-y-3">
            {categoryOrder.map((categoryName) => {
              const categoryRoles = groupedRoles[categoryName];
              if (!categoryRoles || categoryRoles.length === 0) return null;

              const categoryEmoji = getCategoryEmoji(categoryRoles[0].name);

              return (
                <div key={categoryName}>
                  <h4 className="text-xs font-medium text-[#444] mb-2 flex items-center">
                    <span className="text-sm mr-1">{categoryEmoji}</span>
                    {categoryName}
                  </h4>
                  <div className="space-y-2 ml-4">
                    {categoryRoles.map((role) => (
                      <label
                        key={role.id}
                        className={`block p-2 border rounded cursor-pointer transition-all ${
                          selectedRoleId === role.id
                            ? 'border-[#888] bg-[#f5f5f5]'
                            : 'border-[#eaeaea] hover:border-[#eaeaea]'
                        }`}
                      >
                        <div className="flex items-start">
                          <input
                            type="radio"
                            name="role"
                            value={role.id}
                            checked={selectedRoleId === role.id}
                            onChange={(e) => setSelectedRoleId(e.target.value)}
                            className="mt-0.5 h-3 w-3 text-[#171717] focus:ring-0 border-[#eaeaea]"
                          />
                          <div className="ml-2 flex-1">
                            <div className="text-xs font-medium text-[#171717]">
                              {getDisplayRoleName(role.name)}
                            </div>
                            <div className="text-xs text-[#555] mt-0.5">
                              {getRoleDescription(role.name)}
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-[#eaeaea]">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-[#555] border border-[#eaeaea] rounded hover:bg-[#fafafa] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-3 py-1.5 text-xs bg-[#171717] text-white rounded hover:bg-[#333] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Changing...' : 'Change Role'}
          </button>
        </div>
      </div>
    </div>
  );
};
